'use client';

import { QueryKeys } from '@/constants/query-keys';
import { PromotionalContentPlacement } from '@/enums/promotional-content-placement';
import { promotionalContentsApiService } from '@/services/(frontend)/promotional-contents/promotional-contents-api.service';
import { GetPromoContentRequest } from '@/types/paged-requests/promo-content-paged-request';
import { PromoContent, PromoContentConfirmationPageRelationshipType, PromoContentRelationshipType } from '@cinemark/content-api-client';
import { useQuery } from '@tanstack/react-query';
import { useDynamicStaleTime } from '../shared/dynamic-stale-time';

type UsePromotionalContentOptions = {
  confPageRelationType?: PromoContentConfirmationPageRelationshipType;
  pageSize?: number;
  payloadId?: string;
  placement: PromotionalContentPlacement;
  relationType?: PromoContentRelationshipType;
};

/**
 * For PromoContentRelationshipType.Theaters: payloadId will be omitted
 * For other relationship types: payloadId is required
 * For confirmation pages: use confPageRelationType instead of relationType
 */
export const usePromotionalContent = ({
  confPageRelationType,
  pageSize,
  payloadId,
  placement,
  relationType,
}: UsePromotionalContentOptions): ReturnType<typeof useQuery<PromoContent[] | null>> => {
  const updateQueryStaleTime = useDynamicStaleTime();

  return useQuery<PromoContent[] | null>({
    queryFn: async () => {
      let requestParams: GetPromoContentRequest = pageSize != null
        ? { overrideWithPageSize: true, pageSize, placement }
        : { placement };
      if (relationType && relationType !== PromoContentRelationshipType.Unknown) {
        if (relationType === PromoContentRelationshipType.Theaters) {
          requestParams = { ...requestParams, relationType };
        } else if (payloadId) {
          requestParams = { ...requestParams, relationId: payloadId, relationType };
        }
      } else if (confPageRelationType && confPageRelationType !== PromoContentConfirmationPageRelationshipType.Unknown && payloadId) {
        requestParams = { ...requestParams, confPageRelationId: payloadId, confPageRelationType };
      }

      try {
        const response = await promotionalContentsApiService.getPromotionalContents(requestParams);

        if (response.headers) {
          updateQueryStaleTime(
            payloadId
              ? QueryKeys.global.promoContentByRelationId(payloadId, placement, confPageRelationType, pageSize)
              : QueryKeys.global.promoContentByArea(relationType!, placement, pageSize),
            response.headers
          );
        }

        return response.data?.data?.length ? response.data.data : null;
      } catch {
        return null;
      }
    },
    queryKey: payloadId
      ? QueryKeys.global.promoContentByRelationId(payloadId, placement, confPageRelationType, pageSize)
      : QueryKeys.global.promoContentByArea(relationType!, placement, pageSize),
  });
};
